import { useState, useMemo } from 'react'
import {
  Box,
  Heading,
  Divider,
  VStack,
  Checkbox,
  Text,
  Button,
  useToast,
} from '@chakra-ui/react'
import { Formik, Form, FormikProps, FormikHelpers } from 'formik'
import { useQueryClient } from 'react-query'
import { useLocation } from 'react-router-dom'
import useUser from 'hooks/useUser/useUser'
import { UserQueryKey } from 'api/common/routes'
import { Amplitude } from 'utils/amplitude.utils'
import {
  CommunicationsPreferencesUpdateDoneProps,
  NavigationSource,
} from 'utils/amplitude.types'
import { objectToSnakeCase } from 'utils/string.utils'
import { Logger } from 'utils/logger.utils'
import { useFlags } from 'launchdarkly-react-client-sdk'
import {
  CommunicationPreferenceValues,
  CommunicationPreferencesProps,
} from './CommunicationPreferences.types'
import { User } from 'hooks/useUser/useUser'

const CommunicationPreferences: React.FC<CommunicationPreferencesProps> = ({
  user,
  setIsLoading,
}) => {
  const { handleUpdateProfile } = useUser()
  const queryClient = useQueryClient()
  const toast = useToast()
  const location = useLocation()
  const logger = new Logger('CommunicationPreferences')
  const { unsubscribeFromEmails } = useFlags()

  const [isSaving, setIsSaving] = useState(false)
  const [hasUnsubscribedFromAll, setHasUnsubscribedFromAll] = useState(false)

  // Try to figure out how the user got here (from an Unsubscribe link in an email
  // or clicking around the Account area in the app). It'll be useful for analytics.
  const navigationSource = useMemo((): NavigationSource => {
    const queryParams = new URLSearchParams(location.search)
    const fromEmail =
      queryParams.get('source') === 'email' ||
      queryParams.get('utm_source') === 'email' ||
      queryParams.get('from') === 'email'

    return fromEmail ? 'email' : 'app_navigation'
  }, [location.search])

  // Extract the user's current subscription preferences from the user object
  const initialValues = useMemo(
    () => ({
      receivesMedicalAppointmentEmails:
        user.receivesMedicalAppointmentEmails ?? true,
      receivesNutritionAppointmentEmails:
        user.receivesNutritionAppointmentEmails ?? true,
      receivesCareJourneyEmails: user.receivesCareJourneyEmails ?? true,
    }),
    [user]
  )

  // Track preference changes in Amplitude and Datadog
  const trackPreferenceUpdate = (newValues: CommunicationPreferenceValues) => {
    const analyticsData = objectToSnakeCase({
      ...newValues,
      previousMedicalAppointmentEmails:
        initialValues.receivesMedicalAppointmentEmails,
      previousNutritionAppointmentEmails:
        initialValues.receivesNutritionAppointmentEmails,
      previousCareJourneyEmails: initialValues.receivesCareJourneyEmails,
      hasUnsubscribedFromAll: hasUnsubscribedFromAll,
      source: navigationSource,
    }) as CommunicationsPreferencesUpdateDoneProps

    Amplitude.communicationsPreferencesUpdateDone(analyticsData)
    logger.info({
      message: 'Communications preferences updated',
      ...analyticsData,
    })
  }

  // This is the big hairy and scary function that tries to update the user
  //  email subscription preferences via an HTTP PATCH request in the API
  const handleSubmit = async (
    values: CommunicationPreferenceValues,
    { resetForm }: FormikHelpers<CommunicationPreferenceValues>
  ) => {
    setIsSaving(true)
    setIsLoading(true)

    const newValues = {
      receivesMedicalAppointmentEmails: values.receivesMedicalAppointmentEmails,
      receivesNutritionAppointmentEmails:
        values.receivesNutritionAppointmentEmails,
      receivesCareJourneyEmails: values.receivesCareJourneyEmails,
    }

    // Get original data before any updates to restore on error
    const originalUserData = queryClient.getQueryData<User>(UserQueryKey)

    // Optimistic update of the cached User. We'll undo on error
    if (originalUserData) {
      queryClient.setQueryData(UserQueryKey, {
        ...originalUserData,
        ...newValues,
      })
    }

    // Let's do the PATCH of the user in the API
    try {
      await handleUpdateProfile(newValues)
      resetForm({ values: newValues })

      toast({
        title: 'Preferences updated',
        description: 'Your communication preferences have been saved.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })

      trackPreferenceUpdate(newValues) // Tell Amplitude and Datadog
    } catch (error) {
      // Revert optimistic update on error
      if (originalUserData) {
        queryClient.setQueryData(UserQueryKey, originalUserData)
      }

      resetForm({ values: initialValues })

      toast({
        title: 'An error occurred',
        description: 'Unable to update your preferences. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsSaving(false)
      setIsLoading(false)
    }
  }

  if (!unsubscribeFromEmails) {
    // We need to check the feature flag here because users might reach this page
    // through direct links (e.g., from email unsubscribe links) even when the feature
    // is disabled. This can happen during feature flag rollouts or when toggling
    // the feature on/off. Instead of showing a broken form, we display a maintenance message.
    return (
      <Box>
        <Heading as="h5" size="s" fontWeight={'semibold'}>
          Manage your communications
        </Heading>
        <Divider my={2} />
        <Text mt={4} color="gray.600">
          Our mailing service is experiencing issues. Please check back shortly.
        </Text>
      </Box>
    )
  }

  return (
    <Box>
      <Heading as="h5" size="s" fontWeight={'semibold'}>
        Manage your communications
      </Heading>
      <Divider my={2} />

      <Formik<CommunicationPreferenceValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {/*
          With typed Formik, we get proper type checking for all form values and handlers:
          - values: strongly typed to match CommunicationPreferenceValues
          - handleChange: properly typed for our form fields
          - setFieldValue: ensures field names and value types match
          - dirty: whether form has changed from initial values
        */}
        {({
          values,
          handleChange,
          setFieldValue,
          dirty,
        }: FormikProps<CommunicationPreferenceValues>) => (
          <Box maxWidth="800px">
            <Form>
              <VStack align="stretch" spacing={4} mt={4}>
                <Box>
                  <Checkbox
                    name="receivesMedicalAppointmentEmails"
                    isChecked={values.receivesMedicalAppointmentEmails}
                    onChange={(e) => {
                      setHasUnsubscribedFromAll(false)
                      handleChange(e)
                    }}
                  >
                    I want to receive emails about my medical appointments
                  </Checkbox>
                  <Text fontSize="sm" color="gray.500" ml={6}>
                    These messages communicate about medical visit scheduling if
                    you have not yet scheduled
                  </Text>
                </Box>

                <Box>
                  <Checkbox
                    name="receivesNutritionAppointmentEmails"
                    isChecked={values.receivesNutritionAppointmentEmails}
                    onChange={(e) => {
                      setHasUnsubscribedFromAll(false)
                      handleChange(e)
                    }}
                  >
                    I want to receive emails about my nutrition appointments
                  </Checkbox>
                  <Text fontSize="sm" color="gray.500" ml={6}>
                    These messages communicate about nutrition visit scheduling
                    if you have not yet scheduled
                  </Text>
                </Box>

                <Box>
                  <Checkbox
                    name="receivesCareJourneyEmails"
                    isChecked={values.receivesCareJourneyEmails}
                    onChange={(e) => {
                      setHasUnsubscribedFromAll(false)
                      handleChange(e)
                    }}
                  >
                    I want to receive emails about the next steps in my care
                    journey
                  </Checkbox>
                  <Text fontSize="sm" color="gray.500" ml={6}>
                    These messages include lab requests and reminder emails to
                    complete labs or imaging
                  </Text>
                </Box>

                <Box>
                  <Checkbox
                    name="unsubscribeToAll"
                    isChecked={
                      !values.receivesMedicalAppointmentEmails &&
                      !values.receivesNutritionAppointmentEmails &&
                      !values.receivesCareJourneyEmails
                    }
                    onChange={(e) => {
                      // If we click on "unsubscribe all" (checked is true), we want to set all
                      //  the other flags to false (unsubscribe all's opposite)
                      const newValue = e.target.checked ? false : true
                      setHasUnsubscribedFromAll(e.target.checked)
                      setFieldValue(
                        'receivesMedicalAppointmentEmails',
                        newValue
                      )
                      setFieldValue(
                        'receivesNutritionAppointmentEmails',
                        newValue
                      )
                      setFieldValue('receivesCareJourneyEmails', newValue)
                    }}
                  >
                    Unsubscribe to all reminders above
                  </Checkbox>
                </Box>

                <Button
                  type="submit"
                  backgroundColor="var(--blue-dark)"
                  color="white"
                  _hover={{
                    backgroundColor: 'var(--blue-dark-hover)',
                  }}
                  isLoading={isSaving}
                  isDisabled={!dirty}
                  alignSelf="flex-end"
                  mt={{ base: 0, md: 4 }}
                  mr={{ base: 8, md: 0 }}
                  w={20}
                >
                  Save
                </Button>
              </VStack>
            </Form>
          </Box>
        )}
      </Formik>
    </Box>
  )
}

export default CommunicationPreferences
